import React, { useRef, useEffect, useState, KeyboardEvent} from 'react'
import { gsap } from 'gsap'
import TextField from '@mui/material/TextField';
import NumberField from '../../../components/CustomInput/NumberField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { TimePicker } from '@mui/x-date-pickers';
import DateRangeBox from 'devextreme-react/date-range-box';
import moment, { Moment } from 'moment'

import AlarmInfoIconAndTitle from '../AlarmInfoIcon'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

interface Props {
    message:    string
    startTime: Moment | null
    endTime: Moment | null
    startDate: Moment
    endDate: Moment
    maxAlarms: number
    handleAlarmChanges: (type: string, value: string | number | string[] | Moment | null) => void
    alarmType: string
    handleCustomTimeframe: (isCustom: boolean) => void
    handleCustomDateRange: (isCustom: boolean) => void
}

const Page4: React.FC<Props> = ({
    message,
    startDate,
    startTime,
    endDate,
    endTime,
    maxAlarms,
    handleAlarmChanges,
    alarmType,
    handleCustomTimeframe,
    handleCustomDateRange
}) => {

    const setMessageTitleRef = useRef<HTMLHeadingElement>(null)
    const setMaxAlarmsTitleRef = useRef<HTMLHeadingElement>(null)
    const setMessageRef = useRef<HTMLDivElement>(null)
    const setMaxAlarmsRef = useRef<HTMLDivElement>(null)
    const timeframeTitleRef = useRef<HTMLHeadingElement>(null)
    const timeframeRef = useRef<HTMLDivElement>(null)
    const timeframeStartRef = useRef<HTMLDivElement>(null)
    const timeframeEndRef = useRef<HTMLDivElement>(null)
    const dateRangeTitleRef = useRef<HTMLHeadingElement>(null)
    const dateRangeRef = useRef<HTMLDivElement>(null)
    const dateRangePickerRef = useRef<HTMLDivElement>(null)
    const dateRangeComponentRef = useRef(null)

    const selectStyle = {
        width: '100%',
        margin: '20px',
        opacity: 0
    }

    const intl: IntlShape = useIntl()

    const [customTimeframe, setCustomTimeframe] = useState<boolean>(false)
    const [customDateRange, setCustomDateRange] = useState<boolean>(false)


    useEffect(() => {
        const isCancelledRef = { current: false };
        let scheduledTimeoutId: ReturnType<typeof setTimeout>;


        const setMessageTitle = setMessageTitleRef?.current
        const setMessage = setMessageRef?.current
        const maxAlamsTitle = setMaxAlarmsTitleRef?.current
        const maxAlarms = setMaxAlarmsRef?.current
        const timeframeTitle = timeframeTitleRef?.current
        const timeframe = timeframeRef?.current
        const startTime = timeframeStartRef?.current
        const endTime = timeframeEndRef?.current
        const dateRangeTitle = dateRangeTitleRef?.current
        const dateRange = dateRangeRef?.current
        const dateRangePicker = dateRangePickerRef?.current


        const entryGsap = {
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
        }
        const gap = '-=0.3'

        const tl = gsap.timeline()

        tl.to(setMessageTitle, entryGsap)
        tl.to(setMessage, entryGsap, gap)
        tl.to(maxAlamsTitle, entryGsap, gap)
        tl.to(maxAlarms, entryGsap, gap)
        
        tl.to(timeframeTitle, entryGsap, gap)
        tl.to(timeframe, entryGsap, gap)
        tl.to(startTime, entryGsap, gap)
        tl.to(endTime, entryGsap, gap)
        tl.to(dateRangeTitle, entryGsap, gap)
        tl.to(dateRange, entryGsap, gap)
        tl.to(dateRangePicker, entryGsap, gap)


        if (customDateRange) {
            const findCalendarTitles = (): void => {
                if (isCancelledRef.current) return;

                const calendarTitleElements = document.querySelectorAll(
                    '.dx-calendar-navigator .dx-button-content .dx-button-text'
                );

                if (calendarTitleElements.length > 0) {
                    calendarTitleElements.forEach((titleElement: Element) => {
                        const titleSpan = titleElement as HTMLSpanElement;
                        const lastFourCharacters = titleSpan.innerText.slice(titleSpan.innerText.length - 4);
                        const parsedYearValue = parseInt(lastFourCharacters);

                        if (!isNaN(parsedYearValue)) {
                            titleSpan.innerText = titleSpan.innerText.slice(0, titleSpan.innerText.length - 5);
                        }
                    });
                }

                scheduledTimeoutId = setTimeout(() => findCalendarTitles(), 10);
            };

            findCalendarTitles();
        }

        return ():void => {
            isCancelledRef.current = true;
            clearTimeout(scheduledTimeoutId);
        };
       

    }, [customTimeframe, customDateRange])

    //handle Auto set radiogroup open if needed timeframe
    useEffect(() => {
        if (startTime !== null || endTime !== null) {
            setCustomTimeframe(true)
        }
    }, [startTime, endTime])


    //handle Auto set radiogroup open if needed date range
    useEffect(() => {
        if (startDate !== null || endDate !== null) {
            setCustomDateRange(true)
        }
    }, [startDate, endDate])

    const handleCustomTime = (e): void => {
        if (e.target.value === 'custom') {
            setCustomTimeframe(true)
            handleCustomTimeframe(true)
        } else {
            setCustomTimeframe(false)
            handleCustomTimeframe(false)
        }
    }

    const handleDateChanges = (e): void => {
        if (e.target.value === 'custom') {
            setCustomDateRange(true)
            handleCustomDateRange(true)
        } else {
            setCustomDateRange(false)
            handleCustomDateRange(false)
        }
    }

    return (
        <div style={{ padding: 30 } }>
            <div style={{ display: 'flex', width: '100%' }}>
                
                <div style={{flex: 2} }>
                    <h4 ref={setMessageTitleRef}><b><FormattedMessage id="Set Alarm Name / Message" /></b></h4>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div style={selectStyle} ref={setMessageRef}>
                            <AlarmInfoIconAndTitle title={intl.formatMessage({ id: "Alarm Name / Message *" })} text={intl.formatMessage({ id: "This is the message that will appear in the notification that is sent to the alarm contacts, you can add the threshold / value of the alarm message to get the value in the notifications" })} />
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={message}
                                onChange={(e): void => handleAlarmChanges('message', e.target.value)}
                                
                            />
                        </div>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    {alarmType !== 'mnf' && alarmType !== 'noData' && alarmType !== 'temperature' && <div>
                        <h4 ref={setMaxAlarmsTitleRef}><b><FormattedMessage id="Set Max Alarms Per Day" /></b></h4>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div style={selectStyle} ref={setMaxAlarmsRef}>
                                <AlarmInfoIconAndTitle title={intl.formatMessage({ id: "Max Alarms in 24 hour Period" })} text={intl.formatMessage({ id: "The maximum number of times that alarm recipients will receive this alarm in a 24 hour period, this is limited to a max of 3" })} />
                                <NumberField
                                    value={maxAlarms}
                                    onValueChange={(e: number): void => handleAlarmChanges('maxAlarms', e)}
                                    min={1}
                                    max={sessionStorage.getItem('userLevel') === 'identityadmin' ? 30 : 3}
                                    defaultValue={0}
                                    fullWidth
                                    width='100%'
                                    disabled={!message}
                                />
                            </div>
                        </div>
                    </div>}
                </div>
                
            </div>
            <div>
                <h4 ref={timeframeTitleRef}><b><FormattedMessage id="Set Alarm Timeframe" /></b></h4>
                <div style={{ display: 'flex', width: '100%' }}>
                    <div style={selectStyle} ref={timeframeRef}>
                        <AlarmInfoIconAndTitle title={intl.formatMessage({ id: "Receive Alarm Timeframe" })} text={intl.formatMessage({ id: "This allows you to set the timeframe for when an alarm will be checked" })} />
                        <RadioGroup
                            row
                            value={customTimeframe ? 'custom' : '24hrs'}
                            onChange={(e): void => handleCustomTime(e)}
                            
                        >
                            <FormControlLabel value="24hrs" control={<Radio color='secondary' />} label={intl.formatMessage({ id: "Default 24hrs" })} style={{ color: '#3c4858' }} disabled={!message} />
                            <FormControlLabel value="custom" control={<Radio color='secondary' />} label={intl.formatMessage({ id: "Custom" })} style={{ color: '#3c4858' }} disabled={!message} />
                        </RadioGroup>
                    </div>
                    {customTimeframe && <div style={selectStyle} ref={timeframeStartRef}>
                        <p><b><FormattedMessage id="Alarm Start Time" /></b></p>
                        <TimePicker
                            slotProps={{
                                textField: { fullWidth: true }
                               
                            }}
                            ampm={false}
                            sx={{ zIndex: 100000 }}
                            value={moment(startTime, 'HH:mm')}
                            onChange={(e): void => handleAlarmChanges('sendTimeframeStart', e)}
                        />
                    </div>}
                    {customTimeframe && <div style={selectStyle} ref={timeframeEndRef}>
                        <p><b><FormattedMessage id="Alarm End Time" /></b></p>
                        <TimePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            ampm={false}
                            value={moment(endTime, 'HH:mm')}
                            onChange={(e): void => handleAlarmChanges('sendTimeframeEnd', e)}
                        />
                    </div>}
                </div>
            </div>
            <div>
                <h4 ref={dateRangeTitleRef}><b>Set Alarm Date Range</b></h4>
                <div style={{ display: 'flex', width: '100%' }}>
                    <div style={selectStyle} ref={dateRangeRef}>
                        <AlarmInfoIconAndTitle title={intl.formatMessage({ id: "Receive Alarm Date Range" })} text={intl.formatMessage({ id: "This allows you to set the date range for when an alarm will be checked" })} />
                        <RadioGroup
                            row
                            value={customDateRange ? 'custom' : 'Always Active'}
                            onChange={(e): void => handleDateChanges(e)}
                        >
                            <FormControlLabel value="Always Active" control={<Radio color='secondary' />} label={intl.formatMessage({ id: "Always Active" })} style={{ color: '#3c4858' }} disabled={!message} />
                            <FormControlLabel value="custom" control={<Radio color='secondary' />} label={intl.formatMessage({ id: "Custom" })} style={{ color: '#3c4858' }} disabled={!message} />
                        </RadioGroup>
                    </div>
                    {customDateRange && <div style={selectStyle} ref={dateRangePickerRef}>
                        <p><b><FormattedMessage id="Alarm Date Range" /></b></p>
                        <DateRangeBox
                            style={{ height: '55px', zIndex: 10000000000000 }}
                            value={[moment(startDate).toDate(), moment(endDate).toDate()]}
                            displayFormat="dd MMM"
                            onValueChange={(e): void => handleAlarmChanges('dateRange', e)}
                            ref={dateRangeComponentRef}
                            min={moment('01-01', 'MM-DD').toDate()}
                            max={moment('12-31', 'MM-DD').toDate()}
                        />
                    </div>}
                    
                </div>
            </div>
        </div>
    )
}
export default Page4